import React, { useState, useEffect } from "react";
import { Search, ChevronUp, ChevronDown, Plus } from "lucide-react";
import StructuralIcons from "assets/Worker Icons/roofing.svg";
import InstallationIcons from "assets/Worker Icons/handyman.svg";
import FinishingIcons from "assets/Worker Icons/imagesearch_roller.svg";
import SpecializedIcons from "assets/Worker Icons/carpenter.svg";
import EquipmentIcons from "assets/Worker Icons/precision_manufacturing.svg";
import ProjectIcons from "assets/Worker Icons/engineering.svg";
import { BASE_URL } from "utils/constant";

const FieldOfWork = ({ onClose, onSelect }) => {
    const [expanded, setExpanded] = useState("Building and Structural Work");
    const [workData, setWorkData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedWork, setSelectedWork] = useState(null);
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [customWorkName, setCustomWorkName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchWorkNames = async () => {
            try {
                const token = localStorage.getItem("access");
                const response = await fetch(`${BASE_URL}/worker/get-all-workname/`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                const data = await response.json();
                if (data.status) {
                    const groupedData = data.data.reduce((acc, item) => {
                        if (!acc[item.work_category]) {
                            acc[item.work_category] = {
                                name: item.work_category,
                                icon: getCategoryIcon(item.work_category),
                                works: [],
                            };
                        }
                        acc[item.work_category].works.push({ id: item.id, name: item.name });
                        return acc;
                    }, {});
                    setWorkData(Object.values(groupedData));
                } else {
                    setError("Failed to fetch work data");
                }
            } catch (err) {
                setError("Error fetching work data");
            } finally {
                setLoading(false);
            }
        };
        fetchWorkNames();
    }, []);

    const getCategoryIcon = (category) => {
        const icons = {
            "Building and Structural work": StructuralIcons,
            "Installation and System": InstallationIcons,
            "Finishing Work": FinishingIcons,
            "Specialized Trades": SpecializedIcons,
            "Equipment Operation and Maintenance": EquipmentIcons,
            "Project Management and Supervision": ProjectIcons,
        };
        return icons[category];
    };

    const toggleExpand = (categoryName) => {
        setExpanded(expanded === categoryName ? null : categoryName);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value.toLowerCase());
    };

    const handleWorkSelect = (work) => {
        setSelectedWork(work.id);
        onSelect({ id: work.id, name: work.name });
    };

    const createCustomWork = async () => {
        if (!customWorkName.trim()) return;
        setSubmitting(true);
        try {
            const token = localStorage.getItem("access");
            const response = await fetch(`${BASE_URL}/worker/create-custom-work-name/`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ name: customWorkName.trim() }),
            });
            const data = await response.json();
            if (data.status) {
                onSelect({ id: data.data.id, name: customWorkName });
            } else {
                setError("Failed to create custom work field");
            }
        } catch (err) {
            setError("Error creating custom work field");
        } finally {
            setSubmitting(false);
            setShowCustomInput(false);
        }
    };

    const filteredData = workData
        .map((category) => ({
            ...category,
            works: category.works.filter(
                (work) =>
                    work.name.toLowerCase().includes(searchTerm) ||
                    category.name.toLowerCase().includes(searchTerm)
            ),
        }))
        .filter((category) => category.works.length > 0);

    return (
        <div className="w-full">
            <div className="border rounded-[12px] border-gray-300 p-3 bg-white">
                {/* Search Bar */}
                <div className="relative mb-6">
                    <div className="border-2 border-[#7F599D] rounded-lg">
                        <input
                            type="text"
                            placeholder="Search Field of Work"
                            className="w-full pl-12 pr-4 py-2 rounded-lg focus:outline-none bg-white"
                            onChange={handleSearchChange}
                        />
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7F599D]" size={24} />
                    </div>
                </div>

                {/* Custom Work Field Input */}
                {showCustomInput ? (
                    <div className="mb-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
                        <h3 className="text-[#232323] text-[15px] font-medium mb-2">Add Custom Work Field</h3>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={customWorkName}
                                onChange={(e) => setCustomWorkName(e.target.value)}
                                placeholder="Enter work field name"
                                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none bg-gray-100"
                            />
                            <button
                                onClick={createCustomWork}
                                disabled={submitting || !customWorkName.trim()}
                                className="bg-[#3B0069] text-white py-2 px-6 rounded-[12px] hover:bg-purple-800 disabled:bg-gray-300 font-medium text-sm"
                            >
                                {submitting ? "Adding..." : "Add"}
                            </button>
                        </div>
                        <button onClick={() => setShowCustomInput(false)} className="text-gray-600 mt-2 text-sm hover:underline">
                            Cancel
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => setShowCustomInput(true)}
                        className="flex items-center justify-center w-full h-[36px] px-4 py-8 mb-6 bg-[#ECE6F0] rounded-lg text-sm text-[#3B0069] cursor-pointer hover:bg-gray-200"
                    >
                        <span className="flex items-center gap-2">
                            <Plus size={16} />
                            Add Custom Work Field
                        </span>
                    </button>
                )}

                {error && <div className="p-3 mb-4 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>}

                {/* Scrollable List container */}
                <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                    {filteredData.map((category) => (
                        <div key={category.name} className="border-b border-gray-200 last:border-0 pb-3">
                            <button className="w-full flex items-center justify-between py-3" onClick={() => toggleExpand(category.name)}>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#ECE6F0] rounded-full flex items-center justify-center">
                                        <img src={category.icon} alt="" className="w-6 h-6" />
                                    </div>
                                    <span className="text-[15px] font-medium text-[#232323]">{category.name}</span>
                                </div>
                                {expanded === category.name ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                            </button>
                            {expanded === category.name && (
                                <div className="pl-14 pt-2 space-y-3">
                                    {category.works.map((work) => (
                                        <label key={work.id} className="flex items-center gap-3 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="workField"
                                                value={work.id}
                                                checked={selectedWork === work.id}
                                                onChange={() => handleWorkSelect(work)}
                                                className="w-5 h-5 text-[#3B0069] focus:ring-[#7F599D] accent-[#3B0069]"
                                            />
                                            <span className="text-sm text-[#232323]">{work.name}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FieldOfWork;
